import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { signIn, verifyCallbackURL } from "@/api/auth";
import { refreshSession, sessionQuery } from "@/api/session";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/field";
import { AuthLayout } from "@/features/auth/auth-layout";
import { SocialSignIn, isSocialError, socialProblemText } from "@/features/auth/social-sign-in";
import { noindexHead } from "@/features/marketing/seo";
import { socialSignInProblem } from "@bridewell/shared";
import { t } from "@/i18n";
import { safeInternalPath } from "@/lib/utils";

// `error` is set by Better Auth when a verification link is bad (TOKEN_EXPIRED, INVALID_TOKEN) or when a sign-in with
// Apple or Google came back without a session (`signup_disabled`, `account_not_linked`, …: social-sign-in.tsx).
// `redirect` is kept only when it is a path on this origin (never an open redirect).
// `reason: "session"`: the app sent the person here because their session ended (main.tsx).
const search = z.object({
  redirect: z.string().optional().transform(safeInternalPath),
  error: z.string().optional(),
  reason: z.enum(["session"]).optional().catch(undefined),
});

export const Route = createFileRoute("/login")({
  head: () => noindexHead(t("marketing.seo.login")),
  validateSearch: search,
  beforeLoad: async ({ context, search }) => {
    if (await context.queryClient.ensureQueryData(sessionQuery)) throw redirect({ to: search.redirect ?? "/app" });
  },
  component: LoginPage,
});

function LoginPage() {
  const { redirect: target, error: linkError, reason } = Route.useSearch();
  const { queryClient } = Route.useRouteContext();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    isSocialError(linkError) ? socialProblemText(socialSignInProblem(linkError) ?? "failed", "signIn")
      : linkError ? t(linkError === "TOKEN_EXPIRED" ? "auth.verifyLinkExpired" : "auth.verifyLinkInvalid")
      : reason === "session" ? t("auth.sessionEnded") : null,
  );
  const noAccount = isSocialError(linkError) && socialSignInProblem(linkError) === "noAccount";
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    const res = await signIn.email({ email, password, callbackURL: verifyCallbackURL(target) });
    setBusy(false);
    if (res.error) {
      // Correct password, unverified address: the API has just re-sent the link.
      if (res.error.code === "EMAIL_NOT_VERIFIED") { setNotice(t("auth.notVerified", { email })); return; }
      setError(res.error.message ?? t("auth.signInFailed"));
      return;
    }
    await refreshSession(queryClient);
    void navigate({ to: target ?? "/app" });
  };

  return (
    <AuthLayout
      title={t("auth.welcomeBack")}
      subtitle={t("auth.platforms")}
      footer={<>{t("auth.noAccount")} <Link to="/signup" search={{ redirect: target }} className="font-semibold text-accent">{t("auth.signUp")}</Link></>}
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {notice && (
          <p className="rounded-control bg-bg px-3 py-2 text-sm text-text-secondary">
            {notice}{noAccount && <> <Link to="/signup" search={{ redirect: target }} className="font-semibold text-accent">{t("auth.signUp")}</Link></>}
          </p>
        )}
        <SocialSignIn mode="signIn" target={target} />
        <Field label={t("common.email")}><Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label={t("auth.password")}><Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {error && <p className="text-sm text-danger">{error}</p>}
        <Button type="submit" size="lg" loading={busy}>{t("auth.signIn")}</Button>
        <Link to="/forgot-password" className="text-center text-sm text-text-secondary hover:text-accent">{t("auth.forgot")}</Link>
      </form>
    </AuthLayout>
  );
}
